import { CourseScoreByTagReport } from '@/client/dashboard/types/dashboard.type';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import {
  createCustomTooltip,
  hideTooltipForNavigation,
} from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import {
  colorBlackMain,
  colorBlueDark,
  colorBlueDarkest,
  colorGrayDark,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div`
  height: calc(100% - 49px);
  width: 100%;
  min-width: 400px;
`;

const StyledBarChart = styled(Chart)`
  height: 100%;
  width: 100%;
`;

type CourseScoreByTagChartProps = {
  fromDate?: Date;
  toDate?: Date;
  id?: string;
  reportData?: CourseScoreByTagReport[];
  isLoading?: boolean;
};

export const CourseScoreByTagChart = ({
  id,
  reportData,
  isLoading,
}: CourseScoreByTagChartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chartRef = useRef<Chart>(null);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const docStyle = getComputedStyle(document.documentElement);
  const textColor = colorBlackMain(docStyle);
  const textColorSecondary = colorBlackMain(docStyle);

  const hasNoData = !reportData || reportData.length === 0;

  useEffect(() => {
    if (!reportData || reportData.length === 0) return;

    const labels = reportData.map((item) => item.name);
    const scoreData = reportData.map((item) =>
      parseFloat(Math.min(Number(item.score || 0), 100).toFixed(1)),
    );
    const benchmarkData = reportData.map((item) =>
      parseFloat(Math.min(Number(item.benchmark || 0), 100).toFixed(1)),
    );

    const allValues = [...scoreData, ...benchmarkData].filter(
      (val) => val !== null && val !== undefined,
    );
    const maxDataValue = allValues.length > 0 ? Math.max(...allValues) : 100;
    const roundedMax = Math.min(Math.ceil(maxDataValue / 10) * 10 + 10, 100);

    const data = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: t('awareness.analytics.scoreByTopic.quizScore'),
          backgroundColor: colorBlueDark(docStyle),
          hoverBackgroundColor: `color-mix(in srgb, ${colorBlueDark(
            docStyle,
          )}, white 15%)`,
          data: scoreData,
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
        {
          type: 'bar',
          label: t('awareness.analytics.scoreByTopic.benchmark'),
          backgroundColor: colorBlueDarkest(docStyle),
          hoverBackgroundColor: `color-mix(in srgb, ${colorBlueDarkest(
            docStyle,
          )}, white 15%)`,
          data: benchmarkData,
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
      ],
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context: any) =>
            createCustomTooltip(context, t, undefined, false, true),
        },
        legend: {
          labels: {
            color: textColor,
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 10,
            boxHeight: 10,
            padding: 30,
            font: {
              family: platformFontFamily(docStyle),
              size: 13,
            },
          },
          position: 'bottom' as const,
          align: 'start' as const,
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
              size: 12,
            },
            maxRotation: 45,
            minRotation: 0,
          },
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
        y: {
          min: 0,
          max: roundedMax,
          ticks: {
            color: textColorSecondary,
            stepSize: roundedMax <= 20 ? 5 : roundedMax <= 50 ? 10 : 20,
            callback: (value: number) => `${value}%`,
            font: {
              family: platformFontFamily(docStyle),
              size: 12,
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            drawOnChartArea: true,
          },
          border: {
            display: false,
            dash: [5, 5],
          },
        },
      },
      onHover: (event: any, activeElements: any[]) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event: any, activeElements: any[], chart: any) => {
        if (!activeElements || !chart?.canvas) return;

        if (activeElements.length > 0) {
          const tooltipId = `chart-tooltip-${chart.canvas.id || 'default'}`;
          hideTooltipForNavigation(tooltipId);
          navigate(RedirectPaths[RedirectPathsEnum.CONTENT_ANALYTICS]());
        }
      },
    };

    setChartData(data);
    setChartOptions(options);
  }, [reportData, t]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        const chartInstance = (chartRef.current as any).chart;
        if (chartInstance) {
          chartInstance.destroy();
        }
      }
      const tooltipId = `chart-tooltip-${id}-bar-chart`;
      hideTooltipForNavigation(tooltipId);
    };
  }, [id]);

  return (
    <ChartWrapper className="mt-4">
      {isLoading ? (
        <Skeleton height="100%" width="100%" />
      ) : hasNoData ? (
        <FlexContainer height="100%" justify="center" align="center">
          <ChartEmptyState
            type={ChartEmptyStateType.AWT}
            noMargin
            noBorder
            title={t('awareness.analytics.scoreByTopic.empty.title')}
            description={t(
              'awareness.analytics.scoreByTopic.empty.description',
            )}
          />
        </FlexContainer>
      ) : (
        <StyledBarChart
          ref={chartRef}
          type="bar"
          data={chartData}
          options={chartOptions}
          id={`${id}-bar-chart`}
        />
      )}
    </ChartWrapper>
  );
};
